import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import {
  Box, Button, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography,
} from "@mui/material";
import { listComponents, suggestComponent, mapComponent, unmapComponent } from "../api/machineSbomApi";
import { sbomErrorMessage } from "../utils/capabilities";
import {
  Muted, Pager, Pill, WarnNote, cardSx, cellSx, fieldSx, headCellSx, outlineBtnSx, primaryBtnSx,
} from "./sbomUi";

const PAGE = 10;
const RESOLUTION_TONE = { cpe: "success", mapped: "progress", builtin: "low", unresolved: "high" };
const FILTERS = ["all", "unresolved", "mapped", "builtin", "cpe"];

// Suggest -> pick -> confirm, or type the CPE vendor/product/part by hand. Nothing is scanned until the user confirms.
function MapPanel({ accessToken, machineId, component, onDone, onCancel }) {
  const { t } = useTranslation();
  const [candidates, setCandidates] = useState(null);
  const [pick, setPick] = useState("");
  const [manual, setManual] = useState({ cpeVendor: "", cpeProduct: "", cpePart: "a" });
  const [busy, setBusy] = useState(false);
  const ref = { name: component.name, vendor: component.vendor };

  const suggest = async () => {
    setBusy(true);
    try {
      setCandidates(await suggestComponent(accessToken, machineId, ref));
    } catch (err) {
      toast.error(sbomErrorMessage(err, t("sbom.errors.suggest")));
    } finally {
      setBusy(false);
    }
  };
  const save = async (body, source) => {
    setBusy(true);
    try {
      await mapComponent(accessToken, machineId, { ...ref, ...body, source });
      toast.success(t("sbom.components.mapPanel.saved"));
      onDone();
    } catch (err) {
      toast.error(sbomErrorMessage(err, t("sbom.errors.save")));
    } finally {
      setBusy(false);
    }
  };
  const chosen = pick === "" ? undefined : candidates?.[Number(pick)];

  return (
    <Box aria-label={t("sbom.components.mapPanel.aria", { name: component.name })} sx={{ p: 2, my: 1, backgroundColor: "var(--bg-surface)", border: "1px solid var(--border)", borderRadius: "8px", display: "grid", gap: 2 }}>
      <Typography sx={{ fontWeight: 700, color: "var(--text-primary)" }}>{component.name} {component.version}</Typography>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
        <Button size="small" variant="outlined" sx={outlineBtnSx} disabled={busy} onClick={suggest}>{t("sbom.components.mapPanel.suggest")}</Button>
        {candidates && candidates.length === 0 && <Muted>{t("sbom.components.mapPanel.none")}</Muted>}
      </Box>
      {candidates && candidates.length > 0 && (
        <Box component="fieldset" sx={{ border: "none", p: 0, m: 0, display: "grid", gap: 1 }}>
          <Box component="legend" sx={{ color: "var(--text-muted)", fontSize: 13, mb: 0.5 }}>{t("sbom.components.mapPanel.candidatesNote")}</Box>
          {candidates.map((c, i) => (
            <Box component="label" key={`${c.part}:${c.vendor}:${c.product}`} sx={{ fontSize: 13, color: "var(--text-primary)", display: "flex", gap: 1, alignItems: "center", minHeight: 32 }}>
              <input type="radio" name={`cand-${component.name}`} value={i} checked={pick === String(i)} onChange={() => setPick(String(i))} />
              <code>{c.part}:{c.vendor}:{c.product}</code> {c.title && <Muted>{c.title}</Muted>}
            </Box>
          ))}
          <Box><Button size="small" variant="contained" sx={primaryBtnSx} disabled={busy || !chosen} onClick={() => save({ cpeVendor: chosen.vendor, cpeProduct: chosen.product, cpePart: chosen.part }, "confirmed")}>{t("sbom.components.mapPanel.confirm")}</Button></Box>
        </Box>
      )}
      <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", alignItems: "flex-end" }}>
        <TextField size="small" label={t("sbom.components.mapPanel.cpeVendor")} inputProps={{ "aria-label": t("sbom.components.mapPanel.cpeVendor") }} sx={fieldSx} value={manual.cpeVendor} onChange={(e) => setManual({ ...manual, cpeVendor: e.target.value })} />
        <TextField size="small" label={t("sbom.components.mapPanel.cpeProduct")} inputProps={{ "aria-label": t("sbom.components.mapPanel.cpeProduct") }} sx={fieldSx} value={manual.cpeProduct} onChange={(e) => setManual({ ...manual, cpeProduct: e.target.value })} />
        <TextField select size="small" label={t("sbom.components.mapPanel.part")} SelectProps={{ native: true }} InputLabelProps={{ shrink: true }} inputProps={{ "aria-label": t("sbom.components.mapPanel.partAria") }} sx={fieldSx} value={manual.cpePart} onChange={(e) => setManual({ ...manual, cpePart: e.target.value })}>
          <option value="a">{t("sbom.components.mapPanel.partA")}</option>
          <option value="o">{t("sbom.components.mapPanel.partO")}</option>
          <option value="h">{t("sbom.components.mapPanel.partH")}</option>
        </TextField>
        <Button size="small" variant="contained" sx={primaryBtnSx} disabled={busy || !manual.cpeVendor.trim() || !manual.cpeProduct.trim()} onClick={() => save(manual, "manual")}>{t("sbom.components.mapPanel.manualSave")}</Button>
        <Button size="small" variant="outlined" sx={outlineBtnSx} onClick={onCancel}>{t("common.cancel")}</Button>
      </Box>
      <Muted>{t("sbom.components.mapPanel.versionNote", { version: component.version })}</Muted>
    </Box>
  );
}

export default function MachineComponents({ accessToken, machine, onChanged, onClose }) {
  const { t } = useTranslation();
  const [status, setStatus] = useState("all");
  const [offset, setOffset] = useState(0);
  const [data, setData] = useState({ total: 0, items: [], counts: {} });
  const [mapping, setMapping] = useState(null);
  const idle = machine.status !== "queued" && machine.status !== "running";

  const load = useCallback(async () => {
    try {
      setData(await listComponents(accessToken, machine.id, { status, limit: PAGE, offset }));
    } catch (err) {
      toast.error(sbomErrorMessage(err, t("sbom.errors.loadComponents")));
    }
  }, [accessToken, machine.id, status, offset, t]);
  useEffect(() => { load(); }, [load, machine.status, machine.scannable_count]);

  const total = Object.values(data.counts || {}).reduce((a, b) => a + b, 0);
  const unresolved = data.counts?.unresolved || 0;
  const done = () => { setMapping(null); onChanged(); load(); };
  const remove = async (c) => {
    if (!window.confirm(t("sbom.components.confirmUnmap", { name: c.name }))) return;
    try {
      await unmapComponent(accessToken, machine.id, { name: c.name, vendor: c.vendor });
      done();
    } catch (err) {
      toast.error(sbomErrorMessage(err, t("sbom.errors.unmap")));
    }
  };

  return (
    <Box component="section" aria-label={t("sbom.components.aria")} sx={cardSx}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 2 }}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 700, color: "var(--text-primary)" }}>{t("sbom.components.title", { name: machine.machine_name })}</Typography>
          <Muted>{t("sbom.components.coverage", { done: total - unresolved, total })}</Muted>
        </Box>
        <Button size="small" variant="outlined" sx={outlineBtnSx} onClick={onClose}>{t("common.close")}</Button>
      </Box>
      {unresolved > 0 && (
        <Box sx={{ mt: 2 }}><WarnNote role="alert">{t("sbom.components.unresolvedWarn", { count: unresolved })}</WarnNote></Box>
      )}
      {!idle && <Box sx={{ mt: 1 }}><Muted>{t("sbom.components.busy")}</Muted></Box>}
      <Box sx={{ mt: 2 }}>
        <TextField select size="small" label={t("sbom.components.filter")} SelectProps={{ native: true }} InputLabelProps={{ shrink: true }} inputProps={{ "aria-label": t("sbom.components.filter") }}
          sx={{ ...fieldSx, minWidth: 220 }} value={status} onChange={(e) => { setStatus(e.target.value); setOffset(0); }}>
          {FILTERS.map((v) => <option key={v} value={v}>{t(`sbom.components.filters.${v}`)}</option>)}
        </TextField>
      </Box>
      <TableContainer sx={{ mt: 1 }}>
        <Table size="small">
          <TableHead>
            <TableRow>{["component", "version", "vendor", "type", "status", ""].map((h) => <TableCell key={h || "actions"} sx={headCellSx}>{h ? t(`sbom.components.cols.${h}`) : ""}</TableCell>)}</TableRow>
          </TableHead>
          <TableBody>
            {data.items.map((c) => (
              <React.Fragment key={`${c.name}|${c.version}|${c.vendor || ""}`}>
                <TableRow hover data-testid={`component-${c.name}`}>
                  <TableCell sx={{ ...cellSx, fontWeight: 600 }}>{c.name}</TableCell>
                  <TableCell sx={cellSx}>{c.version}</TableCell>
                  <TableCell sx={cellSx}>{c.vendor || "—"}</TableCell>
                  <TableCell sx={cellSx}>{["firmware", "device"].includes(c.type) ? t(`sbom.components.types.${c.type}`) : c.type || "—"}</TableCell>
                  <TableCell sx={cellSx}>
                    <Pill tone={RESOLUTION_TONE[c.resolution]} text={t(`sbom.components.resolution.${c.resolution}`)} />
                    {c.cpe && <Box sx={{ mt: 0.5, wordBreak: "break-all" }}><Muted><code>{c.cpe}</code></Muted></Box>}
                  </TableCell>
                  <TableCell sx={{ ...cellSx, whiteSpace: "nowrap" }}>
                    {c.resolution === "unresolved" && <Button size="small" variant="outlined" sx={outlineBtnSx} disabled={!idle} onClick={() => setMapping(c)}>{t("sbom.components.map")}</Button>}
                    {c.resolution === "mapped" && <Button size="small" variant="outlined" sx={outlineBtnSx} disabled={!idle} onClick={() => remove(c)}>{t("sbom.components.unmap")}</Button>}
                  </TableCell>
                </TableRow>
                {mapping && mapping.name === c.name && (mapping.vendor || null) === (c.vendor || null) && (
                  <TableRow><TableCell colSpan={6} sx={{ borderColor: "var(--border)" }}><MapPanel accessToken={accessToken} machineId={machine.id} component={c} onDone={done} onCancel={() => setMapping(null)} /></TableCell></TableRow>
                )}
              </React.Fragment>
            ))}
            {data.items.length === 0 && <TableRow><TableCell colSpan={6} sx={{ ...cellSx, color: "var(--text-muted)" }}>{t("sbom.components.empty")}</TableCell></TableRow>}
          </TableBody>
        </Table>
      </TableContainer>
      <Pager offset={offset} shown={data.items.length} total={data.total} pageSize={PAGE} onChange={setOffset} />
    </Box>
  );
}
